import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/common/Modal/Modal';
import Input from '@components/Form/Input/Input';
import Textarea from '@components/Form/Textarea/Textarea';
import Button from '@components/Button/Button';
import { applyServerValidationErrors } from '@/utils/apiValidationErrors';

export interface EditCourseForm {
    course_name: string;
    description: string;
    is_active: boolean;
}

export interface EditCourseModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData: EditCourseForm | null;
    onSubmit?: (data: EditCourseForm) => void | Promise<void>;
    isSubmitting?: boolean;
}

const EditCourseModal = ({
    isOpen,
    onClose,
    initialData,
    onSubmit,
    isSubmitting = false,
}: EditCourseModalProps) => {
    const { t } = useTranslation('courses');
    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<EditCourseForm>({
        defaultValues: {
            course_name: '',
            description: '',
            is_active: true,
        },
    });

    useEffect(() => {
        if (isOpen && initialData) {
            reset({
                course_name: initialData.course_name,
                description: initialData.description,
                is_active: initialData.is_active,
            });
        }
    }, [isOpen, initialData, reset]);

    const handleClose = () => {
        clearErrors();
        onClose();
    };

    const handleFormSubmit = async (data: EditCourseForm) => {
        clearErrors();
        try {
            await Promise.resolve(onSubmit?.(data));
        } catch (error) {
            applyServerValidationErrors<EditCourseForm>(error, setError);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={t('modal.edit_title')}>
            <form
                id="edit-course-form"
                onSubmit={handleSubmit(handleFormSubmit)}
                noValidate
                className="overflow-y-auto p-4 2xl:p-5 flex flex-col gap-4"
            >
                <Input
                    label={t('modal.program_name_label')}
                    placeholder={t('modal.program_name_placeholder')}
                    required
                    registration={register('course_name', {
                        required: t('modal.validation.name_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.name_empty'),
                    })}
                    error={errors.course_name?.message}
                />

                <Textarea
                    label={t('modal.description_label')}
                    placeholder={t('modal.description_placeholder')}
                    rows={4}
                    required
                    registration={register('description', {
                        required: t('modal.validation.desc_required'),
                        validate: value =>
                            value.trim().length > 0 ||
                            t('modal.validation.desc_empty'),
                    })}
                    error={errors.description?.message}
                />
            </form>

            <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3">
                <Button type="button" variant="dark" onClick={handleClose} disabled={isSubmitting}>
                    {t('modal.cancel_button')}
                </Button>

                <Button type="submit" variant="primary" form="edit-course-form" disabled={isSubmitting}>
                    {isSubmitting ? t('modal.updating_button') : t('modal.save_button')}
                </Button>
            </div>
        </Modal>
    );
};

export default EditCourseModal;
